/**
 * Ask Hub on the Mac: the shared question step (packages/shared/src/ask.ts) run over the engine, with the matching
 * package scoring introductions. Nothing here guesses; every name in an answer is a row the grammar returned.
 */
import { lexicalScore } from "@hub/matching";
import { askSourceFromEngine, askStarters, askWorkspace, type AskContext, type AskResponse, type AskSource } from "@hub/shared";
import { invokeEngine } from "../_shims";

export type { AskIntent, AskPerson, AskResponse } from "@hub/shared";
export { planQuestion } from "@hub/shared";

/** The engine as Ask Hub reads it; the matching package's lexical scorer ranks introductions. */
export function askSource(now?: () => Date): AskSource {
  return askSourceFromEngine(invokeEngine, { now, scoreIntro: (need, offer) => lexicalScore(need, offer).score });
}

/** Answers a question from the workspace. `ctx.person` is the person the panel is open about. */
export function askHub(question: string, ctx: AskContext = {}): Promise<AskResponse> {
  return askWorkspace(question, askSource(), ctx);
}

/** Starter questions drawn from this workspace's own companies, places, needs, offers and groups. */
export async function loadStarters(): Promise<string[]> {
  try {
    return askStarters(await invokeEngine("getWorkspaceFacets", { limit: 3 }));
  } catch {
    return askStarters({ companies: [], locations: [], groups: [], needs: [], offers: [] });
  }
}

/** Group names, so "who is in Rotary" finds the group however the question is phrased. */
export async function loadGroupNames(): Promise<string[]> {
  try {
    return (await invokeEngine("listGroups", undefined)).filter((g) => !g.isInternal).map((g) => g.name);
  } catch {
    return [];
  }
}
